<div class="search-autocomplete">
  <div class="search-container">
    <mat-form-field appearance="outline" class="search-field">
      <mat-icon matPrefix>search</mat-icon>
      <input
        matInput
        [formControl]="searchControl"
        [placeholder]="placeholder"
        [matAutocomplete]="auto"
        (focus)="expandSearch()"
        type="text"
      />
      @if (searchControl.value) {
        <button
          matSuffix
          mat-icon-button
          aria-label="Clear"
          (click)="clearSearch()"
        >
          <mat-icon>close</mat-icon>
        </button>
      }
      <mat-autocomplete #auto="matAutocomplete" [displayWith]="displayFn">
        @if (isLoading()) {
          <mat-option class="loading-option">
            <span class="loading-text">Loading...</span>
          </mat-option>
        }

        @if (
          searchControl.value && searchControl.value.length < MIN_QUERY_LENGTH
        ) {
          <mat-option disabled class="min-chars-option">
            <span class="min-chars-text"
              >Enter at least {{ MIN_QUERY_LENGTH }} characters to search</span
            >
          </mat-option>
        }

        @if (!isLoading()) {
          <!-- Search error -->
          @if (searchError()) {
            <mat-option disabled class="error-option">
              <span class="error-text">{{ searchError() }}</span>
            </mat-option>
          }
          <!-- Projects -->
          @if (!searchError() && searchResults().projects.length > 0) {
            <div class="option-group-label">Projects</div>
            @for (project of searchResults().projects; track project.projectId) {
              <mat-option
                [value]="project"
                (onSelectionChange)="
                  $event.isUserInput && onOptionSelected(project)
                "
              >
                <div class="option-content">
                  <span
                    class="option-title"
                    [innerHTML]="project.nameSummary"
                  ></span>
                  <span
                    class="option-description"
                    [innerHTML]="project.descriptionSummary"
                  ></span>
                </div>
              </mat-option>
            }
          }
          <!-- Workflows -->
          @if (searchResults().notebooks.length > 0) {
            <div class="option-group-label">Workflows</div>
            @for (notebook of searchResults().notebooks; track notebook.versionId + ':' + notebook.workflowId) {
              <mat-option
                [value]="notebook"
                (onSelectionChange)="
                  $event.isUserInput && onOptionSelected(notebook)
                "
              >
                <div class="option-content">
                  <span
                    class="option-title"
                    [innerHTML]="notebook.nameSummary"
                  ></span>
                  <span class="option-description">{{
                    notebook.projectName
                  }}</span>
                </div>
              </mat-option>
            }
          }
          <!-- Steps -->
          @if (searchResults().chunks.length > 0) {
            <div class="option-group-label">Steps</div>
            @for (chunk of searchResults().chunks; track chunk.versionId + ':' + chunk.stepId) {
              <mat-option
                [value]="chunk"
                (onSelectionChange)="
                  $event.isUserInput && onOptionSelected(chunk)
                "
              >
                <div class="option-content">
                  <span class="option-title">{{ chunk.stepName }}</span>
                  <span class="option-description">
                    {{ chunk.stepType }} in {{ chunk.notebookName }} ({{
                      chunk.projectName
                    }})
                  </span>
                  <span
                    class="option-summary"
                    [innerHTML]="chunk.summary"
                  ></span>
                </div>
              </mat-option>
            }
          }
          <!-- Dappa APIs -->
          @if (searchResults().apis.length > 0) {
            <div class="option-group-label">APIs</div>
            @for (api of searchResults().apis; track api.versionId + ':' + api.apiId) {
              <mat-option
                [value]="api"
                (onSelectionChange)="$event.isUserInput && onOptionSelected(api)"
              >
                <div class="option-content">
                  <span class="option-title" [innerHTML]="api.nameSummary"></span>
                  <span class="option-description">{{ api.projectName }}</span>
                  @if (api.descriptionSummary) {
                    <span
                      class="option-summary"
                      [innerHTML]="api.descriptionSummary"
                    ></span>
                  }
                </div>
              </mat-option>
            }
          }
          <!-- Dappa API endpoints -->
          @if (searchResults().endpoints.length > 0) {
            <div class="option-group-label">Endpoints</div>
            @for (endpoint of searchResults().endpoints; track endpoint.versionId + ':' + endpoint.apiEndpointId) {
              <mat-option
                [value]="endpoint"
                (onSelectionChange)="
                  $event.isUserInput && onOptionSelected(endpoint)
                "
              >
                <div class="option-content">
                  <span class="option-title">
                    <span class="endpoint-method">{{ endpoint.method }}</span>
                    {{ endpoint.path }}
                  </span>
                  <span class="option-description">
                    {{ endpoint.endpointName }} in {{ endpoint.apiName }} ({{
                      endpoint.projectName
                    }}){{ endpoint.matchedIn === 'source' ? ' · handler code' : '' }}
                  </span>
                  <span
                    class="option-summary"
                    [innerHTML]="endpoint.summary"
                  ></span>
                </div>
              </mat-option>
            }
          }
          <!-- No results -->
          @if (
            !searchError() &&
            hasNoResults() &&
            searchControl.value &&
            searchControl.value.length >= MIN_QUERY_LENGTH
          ) {
            <mat-option disabled> No results found </mat-option>
          }
        }
      </mat-autocomplete>
    </mat-form-field>

    <button
      mat-icon-button
      class="settings-button"
      [matMenuTriggerFor]="searchSettings"
      aria-label="Search settings"
    >
      <mat-icon>tune</mat-icon>
    </button>
  </div>

  <mat-menu
    #searchSettings="matMenu"
    class="search-settings-menu"
    [hasBackdrop]="false"
  >
    <div
      class="search-settings-content"
      (click)="$event.stopPropagation()"
      (keydown)="$event.stopPropagation()"
      mat-menu-content
    >
      <h3 class="settings-title">Search Settings</h3>

      <div class="settings-section">
        <label class="settings-label">Search Method</label>
        <mat-button-toggle-group
          [(ngModel)]="searchOptions.method"
          name="searchMethod"
        >
          <mat-button-toggle value="like">Like</mat-button-toggle>
          <mat-button-toggle value="fulltext">Full Text</mat-button-toggle>
          <mat-button-toggle value="regex">Regex</mat-button-toggle>
        </mat-button-toggle-group>
      </div>

      <div class="settings-section">
        <label class="settings-label">Versions</label>
        <mat-button-toggle-group
          [(ngModel)]="searchOptions.versions"
          name="searchVersions"
        >
          <mat-button-toggle value="auto" title="HEAD where you can edit, otherwise the latest published version">Auto</mat-button-toggle>
          <mat-button-toggle value="latest">Latest</mat-button-toggle>
          <mat-button-toggle value="head">Head</mat-button-toggle>
          <mat-button-toggle value="all">All</mat-button-toggle>
        </mat-button-toggle-group>
      </div>

      <div class="settings-section">
        <label class="settings-label">Search In</label>
        <div class="search-in-options">
          <mat-checkbox [(ngModel)]="searchOptions.projectSearch"
            >Projects</mat-checkbox
          >
          <mat-checkbox [(ngModel)]="searchOptions.stepSearch"
            >Steps</mat-checkbox
          >
        </div>
      </div>

      <div class="settings-section">
        <label class="settings-label">Results Limit</label>
        <mat-slider [min]="5" [max]="50" [step]="5" discrete>
          <input matSliderThumb [(ngModel)]="searchOptions.limit" />
        </mat-slider>
        <div class="limit-value">{{ searchOptions.limit }} results</div>
      </div>

      <div class="settings-actions">
        <button mat-button (click)="resetSearchOptions()">Reset</button>
        <button
          mat-raised-button
          color="primary"
          (click)="applySearchOptions()"
        >
          Apply
        </button>
      </div>
    </div>
  </mat-menu>
</div>
